"use client";

import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Row } from "@faire/web/slate/Layout";
import * as React from "react";
import styled, { useTheme } from "styled-components";

import { Chevron } from "../../../icons/foundation/Chevron";
import { Close } from "../../../icons/foundation/Close";

import { ModalNavigationProps } from "./ModalNavigationProps";

export const ModalNavigationFixed: React.FC<ModalNavigationProps> = ({
  onClose,
  showCloseButton,
  onBack,
  onForward,
  primary,
  closeIconProps,
  backIconProps,
  forwardIconProps,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const theme = useTheme();
  const modalTheme = theme.modals?.[primary ? "primary" : "default"];
  const iconColor = modalTheme?.color ?? Core.icon.default;
  const iconProps = modalTheme?.iconProps ?? {};
  return (
    <NavWrapper>
      <Row>
        {onBack && (
          <BackButton
            onClick={onBack}
            aria-label={strictLocalize({
              defaultMessage: "Back",
              description: {
                text: "Label for button goes back in a modal",
              },
            })}
          >
            <Chevron
              color={iconColor}
              direction="left"
              {...iconProps}
              {...backIconProps}
            />
          </BackButton>
        )}
        {onForward && (
          <ForwardButton
            onClick={onForward}
            aria-label={strictLocalize({
              defaultMessage: "Forward",
              description: {
                text: "Label for button goes forward in a modal",
              },
            })}
          >
            <Chevron
              color={iconColor}
              direction="right"
              {...iconProps}
              {...forwardIconProps}
            />
          </ForwardButton>
        )}
      </Row>
      {showCloseButton && (
        <CloseButton
          onClick={onClose}
          aria-label={strictLocalize({
            defaultMessage: "Close",
            description: {
              text: "Label for button that closes a modal",
            },
          })}
        >
          <Close color={iconColor} {...iconProps} {...closeIconProps} />
        </CloseButton>
      )}
    </NavWrapper>
  );
};

const NavWrapper = styled.div`
  position: sticky;
  top: 0;
  background-color: ${Core.surface.inverse};
  padding: 16px 0;
  width: 100%;
  justify-content: space-between;
  display: flex;
  z-index: 100;
`;

const NavButton = styled.button`
  padding: 12px;
  display: flex;
  align-items: center;
  &:focus-visible {
    outline: -webkit-focus-ring-color auto 1px;
    border-radius: 50px;
  }
`;

const BackButton = styled(NavButton)`
  margin-left: 24px;
`;

const ForwardButton = styled(NavButton)`
  margin-left: 36px;
`;

const CloseButton = styled(NavButton)`
  margin-right: 16px;
`;
